import * as ngCore from '@angular/core'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../state'
import * as content from '../../../../content'

/**
 * Displays app pages and permitted app page creation or deletion actions.
 */
@ngCore.Component({
  imports: [
    ds.table.Component,
    ds.overlay.confirmation.Component,
    content.admin.modals.appPage.Component,
  ],
  selector: 'atn-admin-app-page-app-pages-tab',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.admin.appPages.Collection} */
  public readonly appPagesEc = ngCore.inject(state.admin.appPages.Collection)

  /** @see {@link state.admin.contexts.Feature} */
  private readonly _contextsFeature = ngCore.inject(
    state.admin.contexts.Feature,
  )

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link state.admin.pages.Collection} */
  public readonly pagesEc = ngCore.inject(state.admin.pages.Collection)

  /**
   * Exposed page selection target from state
   *
   * @see {@link state.admin.pages.SELECTION_TARGET}
   */
  public readonly selectionTarget = state.admin.pages.SELECTION_TARGET

  /** Whether the current admin user can add app pages */
  public readonly canAddAppPageSg = ngCore.computed(() =>
    state.admin.portalAccess.ContentActionAccess.canCreateChild({
      context: this._contextsFeature.selectors.appContext.asSignal(),
      user: this._meFeature.selectors.currentUser.asSignal(),
    }),
  )

  /** Canonical table input for pages in the current application. */
  public readonly appPagesTableConfigSg = ngCore.computed(
    () =>
      ({
        collection: this.appPagesEc,
        view: {
          creation: this.canAddAppPageSg()
            ? this.addClickHandlerCallback
            : false,
          search: true,
          showColumnSettings: false,
          title: 'Application Pages',
        },
      }) satisfies ds.table.ec.Config.Input<state.admin.appPages.TypeParams>,
  )

  /**
   * Handles the confirmation action for application page deletion.
   */
  public confirmDeletionClickHandler(): void {
    this.appPagesEc.plugins.editingPlugin.feature.actions.submit
      .from('Confirmation.Footer.Buttons.Confirm.Clicked')
      .dispatch({ delete: true })
  }

  /**
   * Handles the click event on the table's add button.
   */
  public addClickHandlerCallback = () => {
    const source = 'Table.AddButton.Clicked'
    if (
      this.appPagesEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity()
        .asSignal()
    ) {
      this.appPagesEc.plugins.editingPlugin.feature.actions.discard
        .from(source)
        .dispatch({
          current: true,
        })
    }

    // Add new app page
    this.appPagesEc.plugins.editingPlugin.feature.actions.edit
      .from(source)
      .dispatch()

    // Clear selected page within app page editing dialog before opening dialog
    this.pagesEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from(source)
      .dispatch({
        key: this.selectionTarget,
        value: { release: true },
      })
  }

  /**
   * Handles the cancellation action for application page deletion.
   */
  public cancelDeletionClickHandler(): void {
    this.appPagesEc.plugins.editingPlugin.feature.actions.discard
      .from('Confirmation.Footer.Buttons.Cancel.Clicked')
      .dispatch({ delete: true })
  }
}
